import React, { useEffect, useState } from 'react'
import { Redirect, useLocation } from 'react-router-dom'

import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import loadable from '@loadable/component'

import { paywallUrls } from '../../paywall-urls'
import { decodeToUserJWT } from '../../types/UserJWTDecoded'
import { getQueryString, getSessionStorageItem, isLocal } from '../../utils'
import { Loader } from '../Loader'
import { ToolFramework404Error } from '../ToolFramework404Error'

const LoadToolsRenderer = loadable.lib<never, ToolsApi>(() => import('@which/tool-framework'), {
  ssr: true,
  fallback: <Loader />,
})

type ToolsApi = {
  RenderTool: (props: ToolProps) => JSX.Element
}

const getIsLoggedIn = (transformTypeDecision: string) =>
  ['AUTHENTICATED_FULL_ACCESS', 'AUTHENTICATED_NO_ACCESS', 'FULL_ACCESS'].includes(
    transformTypeDecision
  )

export const ToolFramework = ({
  toolName,
  optionsJson,
  CRFooter,
  showFeedback,
  toolUrl,
  title,
  questionText,
  standfirst,
  buttonText,
  transformTypeDecision,
  correlationId,
}: ToolProps) => {
  const kinde = useKindeAuth()
  const [paywallState] = useState(paywallUrls())
  const surveySessionData = getSessionStorageItem('impactSurveyData')

  const location = useLocation()

  useEffect(() => {
    if (kinde && kinde.isAuthenticated && paywallState.kindeDomain && paywallState.kindeClientId) {
      kinde.refreshToken({
        domain: paywallState.kindeDomain,
        clientId: paywallState.kindeClientId,
        onRefresh: (data: { success: boolean; accessToken?: string }) => {
          if (data.success && data.accessToken) {
            const domain = isLocal() ? 'localhost' : '.which.co.uk'
            const kindeAccessToken = data.accessToken
            const decoded = decodeToUserJWT(kindeAccessToken)
            const expireDate = new Date(decoded.exp * 1000).toUTCString()
            document.cookie = `sessionId=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate};SameSite=Lax`
            document.cookie = `blaize_session=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate};SameSite=Lax`
          }
        },
      })
    }
  }, [kinde, paywallState])

  if (toolName === 'ImpactSurvey') {
    if (typeof window === 'undefined') {
      return null
    }
    if (
      (!surveySessionData || !surveySessionData.email || !surveySessionData.toolID) &&
      ((typeof window !== 'undefined' && !window.location.search) ||
        !getQueryString('email') ||
        !getQueryString('tool'))
    ) {
      return <ToolFramework404Error message="impact survey error" />
    }
  } else if (
    toolName !== 'EnergyCostCalculator' &&
    toolName !== 'PriceRiseCalculator' &&
    typeof transformTypeDecision === 'undefined'
  ) {
    return <Loader />
  } else if (toolName === 'BookAppointment' && !getIsLoggedIn(transformTypeDecision || '')) {
    return <Redirect to={`/login?return_url=${location.pathname.slice(1)}${location.search}`} />
  }

  return (
    <LoadToolsRenderer>
      {({ RenderTool }) => {
        return (
          <RenderTool
            {...{
              toolName,
              optionsJson,
              CRFooter,
              showFeedback,
              toolUrl,
              title,
              questionText,
              standfirst,
              buttonText,
              correlationId,
            }}
          />
        )
      }}
    </LoadToolsRenderer>
  )
}

type ToolProps = {
  toolName: string
  optionsJson: string
  CRFooter: React.ReactNode
  showFeedback: boolean
  title: string
  toolUrl: string
  questionText: string
  standfirst: string
  buttonText: string
  correlationId: string
  transformTypeDecision?: string
}
